import React, { useState } from 'react';

import BulkUploadBlock from './bulk/BulkUploadBlock';
import GhostButton from './GhostButton';
import SetupCard from './SetupCard';
import StepShell from './StepShell';
import useStepPayload from './useStepPayload';
import AddVenueForm from './venues/AddVenueForm';
import AttachEventForm from './venues/AttachEventForm';

import { useToast } from 'Hooks/useToast';

/**
 * Venues: each venue as a card listing its attached events, with "+ Attach an
 * event to this venue"; "+ Add a venue" by hand; and the venues template for
 * bulk. Every write answers with the whole page.
 */
export default function VenuesStep({ step, urls, deferring, saving, onDefer, onNext, onStatusChange }) {
    const { data, send, load } = useStepPayload(urls.data, onStatusChange);
    const [adding, setAdding] = useState(false);
    const [attachingTo, setAttachingTo] = useState(null);
    const toast = useToast();

    const lede = $t('Add venues and attach your events to them. Needed for venue-level budgets, project plans and R.A.I.D.');

    const write = (request) =>
        send(request).catch((e) => {
            if (e?.response?.status !== 422) {
                toast.error(e?.response?.data?.message || $t('Something went wrong. Please try again.'));
                load();
            }

            throw e;
        });

    const addVenue = (body) => write(() => axios.post(urls.items, body)).then(() => setAdding(false));

    const attach = (venue, sportId) =>
        write(() => axios.post(urls.attach_template.replace('VENUE_ID', venue.id), { sport_id: sportId })).then(() => setAttachingTo(null));

    const imported = (response) => {
        send(
            () => Promise.resolve({ data: response }),
            (body) => body.step,
        ).catch(() => load());
        toast.success(response.result.message);
    };

    if (!data) {
        return (
            <StepShell step={step} lede={lede} deferring={deferring} saving={true} onDefer={onDefer} onNext={() => {}}>
                <p className="tw-text-sm tw-text-charcoal-50">{$t('Loading…')}</p>
            </StepShell>
        );
    }

    return (
        <StepShell step={step} lede={lede} deferring={deferring} saving={saving} onDefer={onDefer} onNext={onNext}>
            <div className="tw-flex tw-flex-col tw-gap-y-2 tw-max-w-2xl">
                {data.venues.length === 0 && !adding && <p className="tw-text-sm tw-text-charcoal-50 tw-mb-0">{$t('No venues yet')}</p>}

                {data.venues.map((venue) => {
                    const regionId = `setup-wizard-venue-${venue.id}-attach`;
                    const attaching = attachingTo === venue.id;

                    return (
                        <SetupCard
                            key={venue.id}
                            heading={venue.name}
                            chip={$t('Venue')}
                            lines={venue.events.map((event) => event.name)}
                            actionLabel={data.can_attach ? $t('+ Attach an event to this venue') : null}
                            expanded={attaching}
                            regionId={regionId}
                            onToggle={() => setAttachingTo(attaching ? null : venue.id)}
                        >
                            <AttachEventForm venue={venue} events={data.events} onSubmit={attach} onCancel={() => setAttachingTo(null)} />
                        </SetupCard>
                    );
                })}

                {data.can_create && adding && (
                    <AddVenueForm venueTypes={data.venue_types} defaultHire={data.default_hire} onSubmit={addVenue} onCancel={() => setAdding(false)} />
                )}

                {data.can_create && (
                    <div className="tw-mt-1">
                        <GhostButton expanded={adding} onClick={() => setAdding((current) => !current)}>
                            {$t('+ Add a venue')}
                        </GhostButton>
                    </div>
                )}
            </div>

            <BulkUploadBlock
                id="setup-wizard-venues-bulk"
                templateUrl={urls.template}
                templateLabel={$t('Download the venues template')}
                importUrl={urls.import}
                canImport={data.can_import}
                onImported={imported}
            />
        </StepShell>
    );
}
